import {
  buildStoreCartDesignGroupMap,
  buildStoreCartMoqDesignGroupMap,
  getStoreCartDesignGroupKey,
  getStoreCartMoqDesignGroupKey,
  type StoreCartDesignGroupMap,
} from './storeCartDesignGroups.utils';
import {
  isCartItemIncludedInStoreOptionSelection,
  type IStoreCartDesignGroupSelection,
} from './storeCartOptionSelection.utils';
import { isStoreCartLineActive } from './storeCartLineSelection.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';
import { DEFAULT_STORE_CART_MOQ_SHIPPING_LANE } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

export {
  isStoreCartLineActive,
  resolveStoreCartItemSelectionFlag,
} from './storeCartLineSelection.utils';

export interface IStoreCartItemUpdatePayload {
  quantity?: number;
  price?: number;
  is_selected?: 0 | 1;
}

export type UpdateStoreCartItemFn = (
  cartItemId: number,
  data: IStoreCartItemUpdatePayload
) => Promise<void>;

export type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

export interface IProductColorMoqInput {
  styleCode: string;
  hexCode: string;
}

export interface IProductColorsMoqRequest {
  colors: IProductColorMoqInput[];
  shippingLane?: StoreCartMoqShippingLane;
}

export interface IProductColorMoqDetail extends IProductColorMoqInput {
  type: string;
  moq: number;
}

export interface IProductColorMoqResult {
  maxMoq: number;
  colors: IProductColorMoqDetail[];
}

interface IProductColorsMoqApiResponse {
  status?: string;
  data?: IProductColorMoqResult;
}

/** Normalize axios MOQ payload — API returns `{ status, data: { maxMoq } }` */
export const parseProductColorsMoqApiResponse = (
  result: unknown
): IProductColorMoqResult | null => {
  if (!result || typeof result !== 'object') {
    return null;
  }

  const wrapped = result as IProductColorsMoqApiResponse;

  if (wrapped.data?.maxMoq != null && Number.isFinite(wrapped.data.maxMoq)) {
    return wrapped.data;
  }

  const direct = result as IProductColorMoqResult;

  if (direct.maxMoq != null && Number.isFinite(direct.maxMoq)) {
    return direct;
  }

  return null;
};

export type FetchProductColorsMoqFn = (
  request: IProductColorsMoqRequest
) => Promise<IProductColorMoqResult | null>;

export const getStoreCartDesignGroupLineIndices = (
  groupFormIndices: number[] | undefined,
  fallbackIndex: number
): number[] => (groupFormIndices?.length ? groupFormIndices : [fallbackIndex]);

export const buildDesignGroupColorMoqInputs = (
  cartItems: ICartItemDetail[]
): IProductColorMoqInput[] => {
  const seen = new Set<string>();
  const inputs: IProductColorMoqInput[] = [];

  cartItems.forEach((item) => {
    const styleCode = item.apparelStyleCode?.trim();
    const hexCode = item.apparelHex?.trim();

    if (!styleCode || !hexCode) {
      return;
    }

    const key = `${styleCode.toUpperCase()}:${hexCode.toLowerCase()}`;

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    inputs.push({ styleCode, hexCode });
  });

  return inputs;
};

export const buildMoqColorsPayloadKey = (colors: IProductColorMoqInput[]): string =>
  colors
    .map(({ styleCode, hexCode }) => `${styleCode}:${hexCode}`)
    .sort()
    .join(',');

/** Wait until every cart line has style/hex so MOQ is fetched once with the full design group */
export const areStoreCartItemsReadyForMoqFetch = (cartItems: ICartItemDetail[]): boolean =>
  cartItems.length > 0 &&
  cartItems.every((item) => Boolean(item.apparelStyleCode?.trim() && item.apparelHex?.trim()));

const inFlightMoqRequests = new Map<string, Promise<IProductColorMoqResult | null>>();

export const fetchProductColorsMoqWithDedup = async (
  request: IProductColorsMoqRequest,
  fetchMoq: FetchProductColorsMoqFn
): Promise<IProductColorMoqResult | null> => {
  const payloadKey = `${
    request.shippingLane ?? DEFAULT_STORE_CART_MOQ_SHIPPING_LANE
  }:${buildMoqColorsPayloadKey(request.colors)}`;
  const inFlight = inFlightMoqRequests.get(payloadKey);

  if (inFlight) {
    return inFlight;
  }

  const requestPromise = fetchMoq(request).finally(() => {
    inFlightMoqRequests.delete(payloadKey);
  });

  inFlightMoqRequests.set(payloadKey, requestPromise);

  return requestPromise;
};

export const getStoreCartDesignGroupMoqRequestKey = (
  cartItems: ICartItemDetail[],
  shippingLane: StoreCartMoqShippingLane = DEFAULT_STORE_CART_MOQ_SHIPPING_LANE
): string => {
  const designGroups = buildStoreCartMoqDesignGroupMap(cartItems);

  const groupKeys = Object.entries(designGroups)
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([groupKey, items]) => {
      const colorInputs = buildDesignGroupColorMoqInputs(items)
        .map(({ styleCode, hexCode }) => `${styleCode}:${hexCode}`)
        .sort()
        .join(',');

      return `${groupKey}:${colorInputs}`;
    })
    .join('|');

  return `${shippingLane}|${groupKeys}`;
};

export const fetchStoreCartDesignGroupsMoq = async (
  designGroupMap: StoreCartDesignGroupMap,
  fetchMoq: FetchProductColorsMoqFn,
  shippingLane: StoreCartMoqShippingLane = DEFAULT_STORE_CART_MOQ_SHIPPING_LANE
): Promise<Record<string, number>> => {
  const payloadGroups = new Map<string, { colors: IProductColorMoqInput[]; groupKeys: string[] }>();

  Object.entries(designGroupMap).forEach(([groupKey, items]) => {
    const colors = buildDesignGroupColorMoqInputs(items);

    if (!colors.length) {
      return;
    }

    const payloadKey = buildMoqColorsPayloadKey(colors);
    const existing = payloadGroups.get(payloadKey);

    if (existing) {
      existing.groupKeys.push(groupKey);
      return;
    }

    payloadGroups.set(payloadKey, { colors, groupKeys: [groupKey] });
  });

  const moqByPayloadKey = new Map<string, number>();

  await Promise.all(
    Array.from(payloadGroups.entries()).map(async ([payloadKey, { colors }]) => {
      const result = await fetchProductColorsMoqWithDedup({ colors, shippingLane }, fetchMoq);

      // maxMoq 0 / API failure → no group floor; UI falls back to default tiers
      if (result?.maxMoq != null && Number.isFinite(result.maxMoq) && result.maxMoq >= 0) {
        moqByPayloadKey.set(payloadKey, result.maxMoq);
      } else {
        moqByPayloadKey.set(payloadKey, 0);
      }
    })
  );

  return Object.fromEntries(
    Array.from(payloadGroups.entries()).flatMap(([payloadKey, { groupKeys }]) => {
      const maxMoq = moqByPayloadKey.get(payloadKey);

      if (maxMoq == null) {
        return [];
      }

      return groupKeys.map((groupKey) => [groupKey, maxMoq] as const);
    })
  );
};

/** Map MOQ fetch keys (proof-level) back to UI design group keys (shell / proof-item keys) */
export const mapMoqResultsToUiDesignGroupKeys = (
  moqsByFetchKey: Record<string, number>,
  cartItems: ICartItemDetail[]
): Record<string, number> => {
  const uiMoqs: Record<string, number> = { ...moqsByFetchKey };

  cartItems.forEach((item) => {
    const fetchKey = getStoreCartMoqDesignGroupKey(item);
    const uiKey = getStoreCartDesignGroupKey(item);
    const maxMoq = moqsByFetchKey[fetchKey];

    if (maxMoq != null) {
      uiMoqs[uiKey] = maxMoq;
    }
  });

  return uiMoqs;
};

export const buildStoreCartItemPersistUpdates = (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[]
): Array<{ cartItemId: number; quantity: number; price: number }> =>
  groupFormIndices
    .map((index) => cartDetails[index])
    .filter((item): item is ICartItemDetail => Boolean(item?.cartItemId))
    .map((item) => {
      const quantity = Number(item.minQuant ?? item.quantity);
      const unitPrice = Number(item.price);
      // Keep quantity persistence even while unit price is still quoting (0) so
      // closing the cart mid-quote does not lose the selected minimum.
      const fallbackPrice = Number(item.apparelPrice ?? item.baseUnitPrice ?? 0);
      const price = unitPrice > 0 ? unitPrice : fallbackPrice;

      return {
        cartItemId: Number(item.cartItemId),
        quantity,
        price,
      };
    })
    .filter(({ quantity, price }) => quantity > 0 && Number.isFinite(price) && price > 0);

export const buildStoreCartOptionInclusionUpdate = (
  item: ICartItemDetail,
  isActive: boolean
): IStoreCartItemUpdatePayload => ({
  quantity: Number(item.minQuant ?? item.quantity),
  price: Number(item.price),
  is_selected: isActive ? 1 : 0,
});

export const findStoreCartColorOptionLine = (
  cartDetails: ICartItemDetail[],
  groupFormIndices: number[],
  designId: number,
  styleCode: string
): ICartItemDetail | undefined => {
  const formIndex = groupFormIndices.find((idx) => {
    const item = cartDetails[idx];

    return item?.designId === designId && item?.apparelStyleCode === styleCode;
  });

  return formIndex == null ? undefined : cartDetails[formIndex];
};

/** Persist color option inclusion via PUT api/cp/cart-item/{id} */
export const persistStoreCartColorOptionInclusion = async (
  cartDetails: ICartItemDetail[],
  groupFormIndices: number[],
  designId: number,
  styleCode: string,
  isActive: boolean,
  updateCartInfo: UpdateStoreCartItemFn
): Promise<ICartItemDetail | undefined> => {
  const item = findStoreCartColorOptionLine(cartDetails, groupFormIndices, designId, styleCode);

  if (!item?.cartItemId) {
    return undefined;
  }

  await updateCartInfo(
    Number(item.cartItemId),
    buildStoreCartOptionInclusionUpdate(item, isActive)
  );

  return item;
};

/** Persist product option inclusion for all lines in a design group via PUT api/cp/cart-item/{id} */
export const persistStoreCartProductOptionInclusion = async (
  cartDetails: ICartItemDetail[],
  groupFormIndices: number[],
  styleCode: string,
  isActive: boolean,
  updateCartInfo: UpdateStoreCartItemFn
): Promise<ICartItemDetail[]> => {
  const items = groupFormIndices
    .map((idx) => cartDetails[idx])
    .filter(
      (item): item is ICartItemDetail =>
        Boolean(item?.cartItemId) && item.apparelStyleCode === styleCode
    );

  if (!items.length) {
    return [];
  }

  await Promise.all(
    items.map((item) =>
      updateCartInfo(Number(item.cartItemId), buildStoreCartOptionInclusionUpdate(item, isActive))
    )
  );

  return items;
};

export const buildStoreCartDesignGroupSelectionUpdates = (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  selection: IStoreCartDesignGroupSelection
): Array<{ cartItemId: number; payload: IStoreCartItemUpdatePayload }> =>
  groupFormIndices
    .map((index) => {
      const item = cartDetails[index];

      if (!item?.cartItemId) {
        return null;
      }

      const shouldBeActive = isCartItemIncludedInStoreOptionSelection(item, selection);
      const isCurrentlyActive = isStoreCartLineActive(item);

      if (shouldBeActive === isCurrentlyActive) {
        return null;
      }

      return {
        cartItemId: Number(item.cartItemId),
        payload: buildStoreCartOptionInclusionUpdate(item, shouldBeActive),
      };
    })
    .filter((update): update is { cartItemId: number; payload: IStoreCartItemUpdatePayload } =>
      Boolean(update)
    );

/** Persist option selection (is_selected) for changed design group lines via PUT api/cp/cart-item/{id} */
export const persistStoreCartDesignGroupSelection = async (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  selection: IStoreCartDesignGroupSelection,
  updateCartInfo: UpdateStoreCartItemFn
): Promise<void> => {
  const updates = buildStoreCartDesignGroupSelectionUpdates(
    groupFormIndices,
    cartDetails,
    selection
  );

  if (!updates.length) {
    return;
  }

  await Promise.all(updates.map(({ cartItemId, payload }) => updateCartInfo(cartItemId, payload)));
};

/** Re-apply persisted option selections to the API after cart reload (e.g. returning from design tool). */
export const reconcileStoreCartOptionSelectionsWithApi = async (
  cartItems: ICartItemDetail[],
  selectionsByGroupKey: Record<string, IStoreCartDesignGroupSelection>,
  updateCartInfo: UpdateStoreCartItemFn
): Promise<void> => {
  const designGroups = buildStoreCartDesignGroupMap(cartItems);

  await Promise.all(
    Object.entries(designGroups).map(async ([groupKey, items]) => {
      const selection = selectionsByGroupKey[groupKey];

      if (
        !selection ||
        (selection.selectedColorKeys.length === 0 &&
          selection.selectedProductStyleCodes.length === 0)
      ) {
        return;
      }

      const groupFormIndices = items.map((item) => item.originalIndex);

      await persistStoreCartDesignGroupSelection(
        groupFormIndices,
        cartItems,
        selection,
        updateCartInfo
      );
    })
  );
};

/** Persist minimum tier quantity and price via PUT api/cp/cart-item/{id} */
export const persistStoreCartDesignGroupItemUpdates = async (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  updateCartInfo: UpdateStoreCartItemFn
): Promise<void> => {
  const updates = buildStoreCartItemPersistUpdates(groupFormIndices, cartDetails);

  if (!updates.length) {
    return;
  }

  await Promise.all(
    updates.map(({ cartItemId, quantity, price }) =>
      updateCartInfo(cartItemId, { quantity, price })
    )
  );
};
